import { useEffect, useMemo, useState, useCallback, useSyncExternalStore } from 'react';
import {
  useGetApproverReport,
  useAIFMUpdateData,
  useGetAIFMJurisdictions,
  useGetAIFMReport,
  useGetJurisdictions,
  useGetLoadAllData,
  useGetReportSchema,
  useUpdateData
} from '../../../../services/report.service';
import {
  filterArrayByFieldsForMultipleTable,
  filterArrayByFieldsNotEmptyValue,
  getDefaultValueByType,
  getKeyByType,
  getTypeFromSchema,
  validateFieldRules
} from '../utils/functions';
import {
  assetTypes,
  macroAssetTypes,
  subAssetTypeFields,
  subAssetTypes,
  turnoverAssetTypes
} from '../../../../consts/assetTypes';
import { strategyTableVariants } from '../consts/tableSections';
import { buildModifiedPayload } from '../../utils/savePayload';
import { REPORT_TYPE_IDS } from '../../consts/reportTypes';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ConfirmSaveToast } from '../../components/ConfirmSaveToast';
import commonStore from '../../../../store/common';
import useHistoryState from '../../../../hooks/useHistoryState';
import { useUserGroupsCurrentUserAdminState } from '../../../../services/user.service';
import { useLoadDataToSnowflake } from '../../../../services/workflow.services';
import { useAutomateReportGeneration } from '../../../../services/automation.service';
import reportStore from '../../store';
import { reaction } from 'mobx';
import { queryClient } from '../../../../config/queryClient';
import { deepCopy } from '../../../../utils/deepCopy';
import { extractApiError } from '../../../../utils';
import { UseReportParams, UseReportReturn } from './types';
import type {
  FundDataRow,
  FundEntry,
  RequiredTable,
  SchemaField
} from '../components/ReportTables/types';
import { syncFundsByVisibleTableKeys } from '../../utils/syncFundsByVisibleTableKeys';

interface SaveOptions {
  callbackFn?: () => void;
}

interface ReportUser {
  role?: string;
}

interface ReportJurisdiction {
  reporting_member_state: string;
  period_id?: string;
  id?: string | number;
}

interface RequiredTableWithName extends RequiredTable {
  table_name: string;
}

interface NormalizedSchemaFieldType {
  type: string;
  required: boolean;
  key: string;
  has_drill_down_feature: boolean;
  options?: unknown[];
  rules?: {
    array?: { enum?: unknown[] };
    string?: { enum?: unknown[]; maxLength?: number; minLength?: number; pattern?: string };
    number?: { maximum?: number; minimum?: number };
    integer?: { maximum?: number; minimum?: number };
  };
}

interface NormalizedSchemaField extends SchemaField {
  types: Record<string, NormalizedSchemaFieldType>;
  defaultValues: Record<string, unknown>;
}

interface ReportSchemaProperty {
  rules?: {
    array?: { enum?: unknown[] };
    string?: { enum?: unknown[]; maxLength?: number; minLength?: number; pattern?: string };
    number?: { maximum?: number; minimum?: number };
    integer?: { maximum?: number; minimum?: number };
  };
  has_drill_down_feature?: boolean;
  [key: string]: unknown;
}

interface ReportSchemaResponseTable {
  required: string[];
  properties: Record<string, ReportSchemaProperty>;
}

type ReportSchemaMap = Record<string, NormalizedSchemaField>;
type FundMap = Record<string, FundEntry>;
type FundInfo = Pick<FundEntry, 'fund_name' | 'fund_id'> & { visibleTableKeys?: string[] };

interface ReportHistoryState {
  funds: FundMap;
}

type ReportApiFund = FundEntry & {
  required_tables?: Record<string, RequiredTable> | RequiredTableWithName[];
  previous_fund?: ReportApiFund;
  difference?: Record<string, unknown>;
};

interface ComparisonReportResult {
  current_fund: ReportApiFund;
  previous_fund?: ReportApiFund;
  difference?: Record<string, unknown>;
}

type LoadReportResult =
  ReportApiFund | ReportApiFund[] | ComparisonReportResult | ComparisonReportResult[];

interface SavePayloadItem {
  fund_id?: string;
  fund_name?: string;
  fund_data: Record<string, FundDataRow[]>;
}

function addItemToArray(arr, index, newItem) {
  return [...arr.slice(0, index + 1), newItem, ...arr.slice(index + 1)];
}

export const useReport = ({
  fundId,
  isStaticTemplate,
  period,
  periodId,
  setSearchParams,
  isAIF001,
  isMultiFunds,
  isLockedPeriod
}: UseReportParams): UseReportReturn => {
  const { reportingMemberState = '', workflowId = '' } = useParams<{
    reportingMemberState?: string;
    workflowId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentDataKey, setCurrentDataKey] = useState<string | undefined>();
  const jurisdictionId = searchParams.get('jurisdictionId');
  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  // const [funds, setFunds] = useState({});
  // const [fundsForAIF, setFundsForAIF] = useState([]);
  const [previousFunds, setPreviousFunds] = useState<FundMap>({});
  const [schema, setSchema] = useState<ReportSchemaMap>({});
  const [hasChanges, setHasChanges] = useState(false);

  const { data: AIFMjurisdictions } = useGetAIFMJurisdictions({
    periodId,
    queryParams: {
      enabled: !!period && !!jurisdictionId
    }
  }) as { data?: ReportJurisdiction[] };

  const currentPeriod = AIFMjurisdictions?.find(
    item => item.reporting_member_state === reportingMemberState.toUpperCase()
  );

  const { data: reportJurisdictions } = useGetJurisdictions({
    periodId,
    queryParams: {
      enabled: !!period && !jurisdictionId
    }
  }) as { data?: ReportJurisdiction[] };

  const jurisdictions = jurisdictionId ? AIFMjurisdictions : reportJurisdictions;

  const {
    state: { funds },
    set,
    undo,
    setInitialState,
    redo,
    canRedo,
    canUndo
  } = useHistoryState<ReportHistoryState>({
    funds: {}
  });

  const { data: user, isLoading: isLoadingUser } = useUserGroupsCurrentUserAdminState() as {
    data?: ReportUser;
    isLoading: boolean;
  };

  const isViewer = useMemo(() => {
    if (user?.role === 'Approver') {
      commonStore.setIsViewer(true);
      return true;
    }
    return false;
  }, [user]);

  // Helper function to create default row with fund info
  const createDefaultRow = useCallback(
    (
      schemaMap: ReportSchemaMap,
      tableKey: string,
      fundInfo: FundInfo,
      existingValue: FundDataRow = {}
    ): FundDataRow => ({
      ...schemaMap[tableKey]?.defaultValues,
      period_id: periodId,
      fund_name: fundInfo.fund_name,
      fund_id: fundInfo.fund_id,
      is_template: isStaticTemplate,
      ...existingValue
    }),
    [periodId, isStaticTemplate]
  );

  // Helper function to create array of rows with ranking
  const createRankedRows = useCallback(
    (
      schemaMap: ReportSchemaMap,
      tableKey: string,
      fundInfo: FundInfo,
      count: number,
      existingData: FundDataRow[] = [],
      useRankingFind: boolean = false
    ): FundDataRow[] => {
      return Array.from({ length: count }, (_, index) => {
        const ranking = `${index + 1}`;
        const existingValue = existingData.find(item => item.ranking === ranking) || {};

        return {
          ...createDefaultRow(schemaMap, tableKey, fundInfo, existingValue),
          ranking
        };
      });
    },
    [createDefaultRow]
  );

  // Helper function to create rows from labels array
  const createLabeledRows = useCallback(
    (
      schemaMap: ReportSchemaMap,
      tableKey: string,
      fundInfo: FundInfo,
      labels: string[],
      labelKey: string,
      existingData: FundDataRow[] = []
    ): FundDataRow[] => {
      return labels.map(label => {
        const existingValue = existingData.find(item => item[labelKey] === label) || {};
        return {
          ...createDefaultRow(schemaMap, tableKey, fundInfo, existingValue),
          [labelKey]: label
        };
      });
    },
    [createDefaultRow]
  );

  // Configuration for tables with fixed row counts
  const tableConfig = useMemo(
    () => ({
      BORROWING_SOURCE: { count: 5 },
      TOP_FIVE_COUNTRIES_EXPOSURE_EXCLUDE_CCP: { count: 5 },
      TOP_FIVE_COUNTRIES_COUNTERPARTY_EXPOSURE: { count: 5 },
      MAIN_INSTRUMENTS: { count: 5 },
      CCP_EXPOSURE: { count: 3 },
      PRINCIPAL_MARKETS: { count: 3 },
      PRINCIPAL_EXPOSURES: { count: 10, useRankingFind: true },
      MOST_IMPORTANT_PORTFOLIO: { count: 5 },
      AIFM_FIVE_PRINCIPAL_MARKET: { count: 5, useRankingFind: true },
      AIFM_PRINCIPAL_INSTRUMENT: { count: 5, useRankingFind: true },
      VALUE_FOR_YEARS_RISK_MEASURE: {
        labels: ['NET_DV01', 'NET_CS01'],
        labelKey: 'risk_measure_type'
      },
      HISTORICAL_PROFILE: {
        labels: ['GROSS', 'NET', 'NAV_CHANGE', 'SUBSCRIPTION', 'REDEMPTION'],
        labelKey: 'transaction_type'
      },
      MARKET_RISK_MEASURE: {
        labels: ['NET_EQTY_DELTA', 'NET_FX_DELTA', 'NET_CTY_DELTA'],
        labelKey: 'risk_measure_type'
      }
    }),
    []
  );

  // Helper function to process INDIVIDUAL_EXPOSURES table
  const processIndividualExposures = useCallback(
    (fundData: FundDataRow[], fundInfo: FundInfo): FundDataRow[] => {
      const groupedData: Record<string, Record<string, Record<string, unknown>>> = {};

      // Build grouped data structure
      Object.keys(macroAssetTypes).forEach(key => {
        groupedData[key] = {};
      });

      Object.keys(assetTypes).forEach(key => {
        const macroKey = key.split('_')[0];
        if (groupedData[macroKey]) {
          groupedData[macroKey][key] = {};
        }
      });

      Object.keys(subAssetTypes).forEach(key => {
        const assetKey = key.split('_').slice(0, 2).join('_');
        const macroKey = key.split('_')[0];
        if (groupedData[macroKey]?.[assetKey]) {
          groupedData[macroKey][assetKey][key] = subAssetTypeFields[key];
        }
      });

      // Build table rows
      const tables: FundDataRow[] = [];
      Object.keys(groupedData).forEach(macroAssetType => {
        Object.keys(groupedData[macroAssetType]).forEach(assetType => {
          Object.keys(groupedData[macroAssetType][assetType]).forEach(subAssetType => {
            const value = fundData.find(item => item.sub_asset_type === subAssetType) || {};
            tables.push({
              period_id: periodId,
              fund_name: fundInfo.fund_name,
              fund_id: fundInfo.fund_id,
              is_template: isStaticTemplate,
              macro_asset_type: macroAssetType,
              asset_type: assetType,
              sub_asset_type: subAssetType,
              ...value
            });
          });
        });
      });

      return tables;
    },
    [periodId, isStaticTemplate]
  );

  // Helper function to process TURNOVER table
  const processTurnover = useCallback(
    (fundData: FundDataRow[], fundInfo: FundInfo): FundDataRow[] => {
      const tables = Object.keys(turnoverAssetTypes).map(subAssetType => {
        const value = fundData.find(item => item.sub_asset_type === subAssetType) || {};
        return {
          period_id: periodId,
          fund_name: fundInfo.fund_name,
          fund_id: fundInfo.fund_id,
          is_template: isStaticTemplate,
          sub_asset_type: subAssetType,
          ...value
        };
      });

      // Handle global errors
      const globalErrors = fundData
        .filter(
          item =>
            !Object.keys(turnoverAssetTypes).includes(String(item.sub_asset_type ?? '')) &&
            item.errors?.sub_asset_type
        )
        .map(item => item.errors?.sub_asset_type)
        .filter(Boolean);

      if (globalErrors.length > 0) {
        tables.forEach(item => {
          const existingGeneral = Array.isArray(item?.errors?.general) ? item.errors.general : [];
          item.errors = {
            ...item.errors,
            general: [...existingGeneral, ...globalErrors]
          };
        });
      }

      return tables;
    },
    [periodId, isStaticTemplate]
  );

  // Helper function to process a single table
  const processTable = useCallback(
    (
      tableKey: string,
      fundData: FundDataRow[] | undefined,
      fundInfo: FundInfo,
      isPreviousFund: boolean = false
    ) => {
      const config = tableConfig[tableKey];
      const existingData = fundData || [];
      const attachVisibility = rows =>
        rows.map(row => ({
          ...row,
          visibleTableKeys: row?.visibleTableKeys || fundInfo.visibleTableKeys || []
        }));

      // Handle INVESTOR_LIQUIDITY_PROFILE or empty arrays

      // Handle tables with fixed counts
      if (config?.count) {
        return attachVisibility(
          createRankedRows(
            schema,
            tableKey,
            fundInfo,
            config.count,
            existingData,
            config.useRankingFind
          )
        );
      }

      // Handle tables with labels
      if (config?.labels) {
        return attachVisibility(
          createLabeledRows(
            schema,
            tableKey,
            fundInfo,
            config.labels,
            config.labelKey,
            existingData
          )
        );
      }

      // Handle INDIVIDUAL_EXPOSURES
      if (tableKey === 'INDIVIDUAL_EXPOSURES') {
        return attachVisibility(processIndividualExposures(existingData, fundInfo));
      }

      // Handle TURNOVER
      if (tableKey === 'TURNOVER') {
        return attachVisibility(processTurnover(existingData, fundInfo));
      }

      if (tableKey === 'INVESTOR_LIQUIDITY_PROFILE' || existingData.length === 0) {
        const existingValue = existingData[0] || {};
        return attachVisibility([createDefaultRow(schema, tableKey, fundInfo, existingValue)]);
      }

      // Default: return existing data
      return attachVisibility(existingData);
    },
    [
      schema,
      tableConfig,
      createDefaultRow,
      createRankedRows,
      createLabeledRows,
      processIndividualExposures,
      processTurnover
    ]
  );

  const onSaveResult = (result: LoadReportResult) => {
    let res: ReportApiFund[] = [];
    if (isComparisonReport) {
      res = isAIF001
        ? [
            {
              ...((result as ComparisonReportResult).current_fund || (result as ReportApiFund)),
              previous_fund: (result as ComparisonReportResult).previous_fund,
              difference: (result as ComparisonReportResult).difference || {}
            }
          ]
        : (result as ComparisonReportResult[]).map(item => ({
            ...item.current_fund,
            previous_fund: item.previous_fund,
            difference: item.difference
          }));
    } else {
      res = isAIF001 ? [result as ReportApiFund] : (result as ReportApiFund[]);
    }

    if (isAIF001 || jurisdictionId) {
      setCurrentDataKey('AIFOO1');
    } else {
      const isStaleDataKey = currentDataKey && !res.some(item => item?.fund_id === currentDataKey);
      if (
        (!currentDataKey || currentDataKey === 'AIFOO1' || isStaleDataKey) &&
        !fundId &&
        !isAIF001
      ) {
        setCurrentDataKey(res[0]?.fund_id);
        setSearchParams(
          prev => {
            prev.set('fund_id', res[0]?.fund_id!);
            return prev;
          },
          { replace: true }
        );
        // setSearchParams({ fund_id: res[0].fund_id });
      }
    }

    const _funds: Record<string, ReportApiFund> = {};

    // Process funds data
    res.forEach(item => {
      const fundKey = isAIF001 ? 'AIFOO1' : item.fund_id!;
      const normalizedRequiredTables: Record<string, RequiredTable> = Array.isArray(
        item.required_tables
      )
        ? item.required_tables.reduce<Record<string, RequiredTable>>((acc, table) => {
            acc[table.table_name] = table;
            return acc;
          }, {})
        : item.required_tables && typeof item.required_tables === 'object'
          ? item.required_tables
          : {};
      const fundData = {
        ...item,
        visibleTableKeys: Object.keys(item.fund_data || {}),
        required_tables: normalizedRequiredTables
      };

      _funds[fundKey] = fundData;
    });

    Object.keys(_funds).forEach(fundKey => {
      const fund = _funds[fundKey];
      fund.isSelected = true;

      if (isComparisonReport) {
        fund.previous_fund = {
          fund_id: fund.previous_fund?.fund_id || fund.fund_id,
          fund_name: fund.previous_fund?.fund_name || fund.fund_name,
          period: fund.previous_fund?.period || '',
          year: fund.previous_fund?.year || '',
          fund_data: fund.previous_fund?.fund_data || {},
          visibleTableKeys: Object.keys(fund.previous_fund?.fund_data || {})
        };
      }
    });

    syncFundsByVisibleTableKeys(_funds, { includePreviousFund: isComparisonReport });

    // Process all tables for each fund
    Object.keys(_funds).forEach(fundKey => {
      const fund = _funds[fundKey];

      const fundInfo = {
        fund_name: fund.fund_name,
        fund_id: fund.fund_id,
        visibleTableKeys: (fund.visibleTableKeys as string[] | undefined) || []
      };

      Object.keys(fund.fund_data).forEach(tableKey => {
        // Process current fund data
        fund.fund_data[tableKey] = processTable(
          tableKey,
          fund.fund_data[tableKey],
          fundInfo,
          false
        );

        // Process previous fund data if it exists
        if (fund.previous_fund?.fund_data?.[tableKey]) {
          const previousFundInfo = {
            fund_name: fund.previous_fund.fund_name,
            fund_id: fund.previous_fund.fund_id,
            visibleTableKeys: (fund.previous_fund.visibleTableKeys as string[] | undefined) || []
          };
          fund.previous_fund.fund_data[tableKey] = processTable(
            tableKey,
            fund.previous_fund.fund_data[tableKey],
            previousFundInfo,
            true
          );
        }
      });
    });

    // All table processing is now handled by processTable helper function above

    // setFunds(_funds);

    commonStore.setFundsSize(Object.keys(_funds).length);

    setInitialState({
      funds: _funds
    });

    setPreviousFunds(_funds);

    toast.remove();
  };

  const report = useGetLoadAllData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      report_type: isAIF001 ? REPORT_TYPE_IDS.AIF001 : REPORT_TYPE_IDS.AIF002,
      reporting_member_state: reportingMemberState.toUpperCase(),
      comparison_mode: isComparisonReport
    },
    queryParams: {
      enabled:
        !!(Object.keys(schema).length > 0) &&
        !isViewer &&
        !(
          period?.data?.modeling_status === 'Not started' &&
          period?.data?.report_progress_status === 'Not started'
        ) &&
        !jurisdictionId,
      onSuccess: onSaveResult,
      cacheTime: 0,
      staleTime: 0
    }
  });

  const approverReport = useGetApproverReport({
    periodId,
    params: {
      is_template: isStaticTemplate,
      report_type: isAIF001 ? REPORT_TYPE_IDS.AIF001 : REPORT_TYPE_IDS.AIF002,
      reporting_member_state: reportingMemberState.toUpperCase(),
      comparison_mode: isComparisonReport
    },
    queryParams: {
      enabled: !!(Object.keys(schema).length > 0) && isViewer,
      onSuccess: onSaveResult,
      cacheTime: 0,
      staleTime: 0
    }
  });

  // const refetch = isViewer ? approverReport.refetch : report.refetch;
  // const isLoading = isViewer ? approverReport.isLoading : report.isLoading;
  // const isFetching = isViewer ? approverReport.isFetching : report.isFetching;

  const aifmReport = useGetAIFMReport({
    periodId,
    params: {
      jurisdiction_id: jurisdictionId,
      is_template: isStaticTemplate
    },
    queryParams: {
      onSuccess: onSaveResult,
      enabled: !!(Object.keys(schema).length > 0) && !!jurisdictionId && !isViewer
    }
  });

  const isLoading = isViewer
    ? approverReport.isLoading
    : jurisdictionId
      ? aifmReport.isLoading
      : report.isLoading;

  const refetch = isViewer
    ? approverReport.refetch
    : jurisdictionId
      ? aifmReport.refetch
      : report.refetch;

  const isFetching = isViewer
    ? approverReport.isFetching
    : jurisdictionId
      ? aifmReport.isFetching
      : report.isFetching;

  const reportLoadError = isViewer
    ? approverReport.error
    : jurisdictionId
      ? aifmReport.error
      : report.error;
  const isReportLoadError = isViewer
    ? approverReport.isError
    : jurisdictionId
      ? aifmReport.isError
      : report.isError;

  const loadDataToSnowflake = useLoadDataToSnowflake();
  const automateReportGeneration = useAutomateReportGeneration();

  useEffect(() => {
    if (
      period?.data?.modeling_status === 'Not started' &&
      period?.data?.report_progress_status === 'Not started' &&
      !isViewer &&
      !isStaticTemplate
    ) {
      // Initial periods (template_type === 'sratch') go through the
      // load-data-to-snowflake pipeline; automation periods use the
      // automation-report endpoint.
      const isInitialPeriod = period?.data?.template_type === 'sratch';
      const promise = isInitialPeriod
        ? loadDataToSnowflake.mutateAsync(periodId, {
            onSuccess: () => {
              refetch();
              period?.refetch?.();
            }
          })
        : automateReportGeneration.mutateAsync(
            { period_id: periodId },
            {
              onSuccess: () => {
                refetch();
                period?.refetch?.();
              }
            }
          );
      toast.promise(promise, {
        loading: 'Generating report...',
        success: 'Рeport was successfully generated',
        error: 'Something was wrong while generating report'
      });
    }
  }, [period?.data, isStaticTemplate]);

  const { mutateAsync: mutateAsyncReport, isLoading: isLoadingDataReport } = useUpdateData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      reporting_member_state: reportingMemberState.toUpperCase()
    }
  });

  const { mutateAsync: mutateAsyncAIFM, isLoading: isLoadingDataAIFM } = useAIFMUpdateData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      jurisdiction_id: jurisdictionId
    }
  });

  const mutateAsync = (jurisdictionId ? mutateAsyncAIFM : mutateAsyncReport) as unknown as (
    data: SavePayloadItem[]
  ) => Promise<{ isSuccess?: boolean }>;
  const isLoadingData = jurisdictionId ? isLoadingDataAIFM : isLoadingDataReport;

  const { isLoading: isLoadingSchema } = useGetReportSchema({
    periodId: jurisdictionId ? currentPeriod?.period_id : periodId,
    params: {
      reporting_member_state: reportingMemberState.toUpperCase()
    },
    queryParams: {
      enabled: jurisdictionId ? !!currentPeriod : !!periodId,
      onSuccess: (res: Record<string, ReportSchemaResponseTable>) => {
        const _res: ReportSchemaMap = {};
        Object.keys(res).forEach(key => {
          const defaultValues: Record<string, unknown> = {};
          const types: Record<string, NormalizedSchemaFieldType> = {};
          Object.keys(res[key].properties).forEach(element => {
            defaultValues[element] = getDefaultValueByType(res[key].properties[element]);
            const _type = getTypeFromSchema(res[key].properties[element]);
            const additionalValues: Partial<Pick<NormalizedSchemaFieldType, 'options'>> = {};
            if (_type === 'select') {
              additionalValues.options =
                res[key].properties[element].rules?.array?.enum ||
                res[key].properties[element].rules?.string?.enum;
            }
            types[element] = {
              type: _type as string,
              required: isStaticTemplate ? false : res[key].required.includes(element),
              key: getKeyByType(_type) as string,
              has_drill_down_feature: res[key].properties[element].has_drill_down_feature || false,
              rules: res[key].properties[element].rules,
              ...additionalValues
            };
          });
          _res[key] = {
            defaultValues: defaultValues,
            types
          };
        });

        setSchema(_res);
      },
      onError: error => {
        // Legacy direct error toasts are intentionally avoided here.
      }
    }
  });

  useEffect(() => {
    if (fundId) {
      setCurrentDataKey(fundId);
    }
  }, [fundId, isAIF001]);

  useEffect(() => {
    const handleBeforeUnload = async e => {
      if (!hasChanges || isLockedPeriod) return;

      const confirmationMessage = 'You have unsaved data. Are you sure want to proceed?';
      e.returnValue = confirmationMessage;
      return confirmationMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges, isLockedPeriod]);

  const onChangeFund = async (key, value, fundId) => {
    const _funds = deepCopy(funds);
    const tableTypes = schema[key]?.types || {};

    const validatedValue = value.map(row => {
      const rowErrors = { ...(row.errors || {}) };

      Object.keys(tableTypes).forEach(fieldName => {
        const error = validateFieldRules(row[fieldName], tableTypes[fieldName]?.rules);

        if (error) {
          rowErrors[fieldName] = { msg: error };
        } else {
          delete rowErrors[fieldName];
        }
      });
      return { ...row, errors: rowErrors };
    });

    _funds[fundId || currentDataKey].fund_data[key] = validatedValue;
    set({
      funds: _funds
    });
    setHasChanges(true);
  };

  const addRow = (key, index) => {
    const _funds = deepCopy(funds);

    Object.keys(funds).forEach(fundId => {
      _funds[fundId].fund_data[key] = addItemToArray(
        _funds[isMultiFunds ? fundId : currentDataKey!].fund_data[key],
        index,
        {
          ...schema[key]?.defaultValues,
          period_id: periodId,
          fund_name: _funds[isMultiFunds ? fundId : currentDataKey!].fund_name,
          fund_id: _funds[isMultiFunds ? fundId : currentDataKey!].fund_id
        }
      );
    });

    set({
      funds: _funds
    });
    setHasChanges(true);
  };

  const onDeleteRow = (key, index) => {
    const _funds = deepCopy(funds);

    Object.keys(funds).forEach(fundId => {
      _funds[fundId].fund_data[key] = _funds[fundId].fund_data[key].map((item, ind) =>
        ind === index
          ? {
              ...item,
              is_deleted: true
            }
          : item
      );
    });

    set({
      funds: _funds
    });
    setHasChanges(true);
  };

  const onClearStaticData = () => {
    const _funds = deepCopy(funds);
    if (isMultiFunds) {
      Object.keys(funds).forEach(_fundId => {
        const values = deepCopy(funds[_fundId]);
        Object.keys(values.fund_data).map(tableName => {
          const table = values.fund_data[tableName];
          table.forEach(item => {
            Object.keys(item).forEach(fieldName => {
              // TO DO: refactor conditions -> create constant vars for strings
              if (
                tableName === 'MAIN_INSTRUMENTS' ||
                tableName === 'MOST_IMPORTANT_PORTFOLIO' ||
                tableName === 'DOMINANT_INFLUENCES'
              ) {
                if (
                  fieldName !== 'period_id' &&
                  fieldName !== 'fund_id' &&
                  fieldName !== 'fund_name' &&
                  fieldName !== 'id'
                ) {
                  item[fieldName] = typeof item[fieldName] === 'string' ? '' : null;
                }
              } else {
                if (
                  fieldName !== 'period_id' &&
                  fieldName !== 'fund_id' &&
                  fieldName !== 'fund_name' &&
                  fieldName !== 'sub_asset_type' &&
                  fieldName !== 'asset_type' &&
                  fieldName !== 'macro_asset_type' &&
                  fieldName !== 'transaction_type' &&
                  fieldName !== 'risk_measure_type' &&
                  fieldName !== 'id'
                ) {
                  item[fieldName] = typeof item[fieldName] === 'string' ? '' : null;
                }
              }
            });
          });
        });
        _funds[_fundId] = values;
      });
    } else {
      const values = deepCopy(funds[currentDataKey!]);
      Object.keys(values.fund_data).map(tableName => {
        const table = values.fund_data[tableName];
        table.forEach(item => {
          Object.keys(item).forEach(fieldName => {
            // TO DO: refactor conditions -> create constant vars for strings
            if (
              tableName === 'MAIN_INSTRUMENTS' ||
              tableName === 'MOST_IMPORTANT_PORTFOLIO' ||
              tableName === 'DOMINANT_INFLUENCES'
            ) {
              if (
                fieldName !== 'period_id' &&
                fieldName !== 'fund_id' &&
                fieldName !== 'fund_name' &&
                fieldName !== 'id'
              ) {
                item[fieldName] = typeof item[fieldName] === 'string' ? '' : null;
              }
            } else {
              if (
                fieldName !== 'period_id' &&
                fieldName !== 'fund_id' &&
                fieldName !== 'fund_name' &&
                fieldName !== 'sub_asset_type' &&
                fieldName !== 'asset_type' &&
                fieldName !== 'macro_asset_type' &&
                fieldName !== 'transaction_type' &&
                fieldName !== 'risk_measure_type' &&
                fieldName !== 'id'
              ) {
                item[fieldName] = typeof item[fieldName] === 'string' ? '' : null;
              }
            }
          });
        });
      });

      _funds[currentDataKey!] = values;
    }

    set({
      funds: _funds
    });
    // setFunds(_funds);
  };

  const onChangeDataKey = async value => {
    if (!isViewer) {
      if ((isFetching && !isLoading) || isLoadingData) return;
      if (hasChanges && !isLockedPeriod) {
        toast.remove();
        toast.custom(
          () => (
            <ConfirmSaveToast
              onSave={() => {
                toast.remove();
                onSave({
                  callbackFn: function () {
                    setSearchParams(
                      prev => {
                        prev.set('fund_id', value);
                        return prev;
                      },
                      { replace: true }
                    );
                    // setSearchParams({ fund_id: value });
                    setCurrentDataKey(value);
                  }
                });
              }}
              onDiscard={() => {
                // setSearchParams({ fund_id: value });
                setSearchParams(
                  prev => {
                    prev.set('fund_id', value);
                    return prev;
                  },
                  { replace: true }
                );
                setCurrentDataKey(value);
                revertDiscardChanges();
                toast.remove();
              }}
            />
          ),
          {
            duration: 100000
          }
        );
        return;
      }
    }

    // setSearchParams({ fund_id: value });
    setSearchParams(
      prev => {
        prev.set('fund_id', value);
        return prev;
      },
      { replace: true }
    );
    setCurrentDataKey(value);
  };

  const onSave = ({ callbackFn }: SaveOptions = {}) => {
    const payload = buildModifiedPayload({
      funds,
      baselineFunds: previousFunds,
      schema,
      isMultiFunds,
      isAIF001,
      currentDataKey,
      strategyTableKeys: strategyTableVariants.map(item => item.key),
      filterNotEmpty: filterArrayByFieldsNotEmptyValue,
      filterMultiple: filterArrayByFieldsForMultipleTable
    });

    toast.promise(mutateAsync(payload), {
      loading: 'Saving...',
      success: () => {
        if (!isStaticTemplate) toast.loading('Validating...');
        async function loadData() {
          const result = await refetch();
          queryClient.refetchQueries(['GET_REPORT_ERRORS']);
          setHasChanges(false);
          if (result.isSuccess && callbackFn) callbackFn();
        }
        loadData();
        return 'Saved successfuly!';
      },
      error: err => extractApiError(err, 'Unable to save the report.')
    });
  };

  const onNavigateOtherReport = async value => {
    if (reportingMemberState === value.reporting_member_state.toLowerCase()) {
      return;
    }
    if (!isViewer) {
      if ((isFetching && !isLoading) || isLoadingData) return;
      if (hasChanges && !isLockedPeriod) {
        toast.remove();
        toast.custom(
          () => (
            <ConfirmSaveToast
              onSave={() => {
                toast.remove();
                onSave({
                  callbackFn: function () {
                    navigate(
                      `/report/${value.reporting_member_state === 'UK' ? 'fca' : 'esma'}/${value.reporting_member_state.toLowerCase()}/${workflowId}/${periodId}${searchParams.get('reportType') === 'grouped' ? `?reportType=grouped${jurisdictionId ? `&jurisdictionId=${value.id}` : ''}` : ''}`
                    );
                  }
                });
              }}
              onDiscard={() => {
                navigate(
                  `/report/${value.reporting_member_state === 'UK' ? 'fca' : 'esma'}/${value.reporting_member_state.toLowerCase()}/${workflowId}/${periodId}${searchParams.get('reportType') === 'grouped' ? `?reportType=grouped${jurisdictionId ? `&jurisdictionId=${value.id}` : ''}` : ''}`
                );
                revertDiscardChanges();
                toast.remove();
              }}
            />
          ),
          {
            duration: 100000
          }
        );
        return;
      }
    }

    navigate(
      `/report/${value.reporting_member_state === 'UK' ? 'fca' : 'esma'}/${value.reporting_member_state.toLowerCase()}/${workflowId}/${periodId}${searchParams.get('reportType') === 'grouped' ? `?reportType=grouped${jurisdictionId ? `&jurisdictionId=${value.id}` : ''}` : ''}`
    );
  };

  const onChangeSelectedReportType = async val => {
    if (!isViewer) {
      if ((isFetching && !isLoading) || isLoadingData) return;
      if (hasChanges && !isLockedPeriod) {
        toast.remove();
        toast.custom(
          () => (
            <ConfirmSaveToast
              onSave={() => {
                toast.remove();
                onSave({
                  callbackFn: function () {
                    setSearchParams(
                      prev => {
                        prev.set('type', val);
                        return prev;
                      },
                      { replace: true }
                    );
                  }
                });
              }}
              onDiscard={() => {
                setSearchParams(
                  prev => {
                    prev.set('type', val);
                    return prev;
                  },
                  { replace: true }
                );
                revertDiscardChanges();
                toast.remove();
              }}
            />
          ),
          {
            duration: 100000
          }
        );
        return;
      }
    }

    setSearchParams(
      prev => {
        prev.set('type', val);
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeSelectedFunds = async (fundId: string) => {
    if (fundId === 'all') {
      if (Object.values(funds).every(fund => fund.isSelected)) {
        const newFunds = Object.keys(funds).reduce<FundMap>((acc, key) => {
          if (key === currentDataKey) {
            acc[key] = {
              ...funds[key],
              isSelected: true
            };
          } else {
            acc[key] = {
              ...funds[key],
              isSelected: false
            };
          }
          return acc;
        }, {});
        setInitialState({ funds: newFunds });
      } else {
        const newFunds = Object.keys(funds).reduce<FundMap>((acc, key) => {
          acc[key] = {
            ...funds[key],
            isSelected: true
          };
          return acc;
        }, {});
        setInitialState({ funds: newFunds });
      }
      return;
    }

    const toggleFundSelection = () => {
      setInitialState({
        funds: {
          ...funds,
          [fundId]: {
            ...funds[fundId],
            isSelected: !funds[fundId].isSelected
          }
        }
      });
    };

    const isCurrentlyDisplayedFund = fundId === currentDataKey;
    const currentFund = funds[fundId];
    const isCurrentlySelected = currentFund.isSelected;

    if (isCurrentlyDisplayedFund) {
      // Toggling the currently displayed fund
      if (isCurrentlySelected) {
        // Deselecting the currently displayed fund - switch to another if available
        const otherSelectedFunds = (Object.values(funds) as ReportApiFund[]).filter(
          fund => fund.isSelected && fund.fund_id !== fundId
        );

        if (otherSelectedFunds.length > 0) {
          // Switch to the first available selected fund before deselecting
          const nextFund = otherSelectedFunds[0];
          setCurrentDataKey(nextFund.fund_id);
          setSearchParams(
            prev => {
              prev.set('fund_id', nextFund.fund_id!);
              return prev;
            },
            { replace: true }
          );
          toggleFundSelection();
        }
        // If no other funds are selected, don't allow deselecting the last one
      } else {
        // Selecting the currently displayed fund - always allow
        toggleFundSelection();
      }
    } else {
      // Toggling a fund that's not currently displayed
      if (isCurrentlySelected) {
        // Deselecting a non-displayed fund - ensure at least one remains selected
        const otherSelectedFunds = Object.values(funds).filter(
          fund => fund.isSelected && fund.fund_id !== fundId
        );

        if (otherSelectedFunds.length >= 1) {
          // Allow deselection if at least one other fund will remain selected
          toggleFundSelection();
        }
        // If this is the only other selected fund, don't allow deselecting
      } else {
        // Selecting a fund - always allow
        toggleFundSelection();
      }
    }
  };

  const revertDiscardChanges = useCallback(() => {
    if (Object.keys(previousFunds || {}).length > 0) {
      setInitialState({
        funds: deepCopy(previousFunds)
      });
    }
    setHasChanges(false);
  }, [previousFunds, setInitialState]);

  const withUnsavedChangesGuard = useCallback(
    action => {
      if (!isViewer) {
        if ((isFetching && !isLoading) || isLoadingData) return;
        if (hasChanges && !isLockedPeriod) {
          toast.remove();
          toast.custom(
            () => (
              <ConfirmSaveToast
                onSave={() => {
                  toast.remove();
                  onSave({
                    callbackFn: function () {
                      action?.();
                    }
                  });
                }}
                onDiscard={() => {
                  revertDiscardChanges();
                  toast.remove();
                  action?.();
                }}
              />
            ),
            {
              duration: 100000
            }
          );
          return;
        }
      }

      action?.();
    },
    [
      hasChanges,
      isLockedPeriod,
      isViewer,
      isFetching,
      isLoading,
      isLoadingData,
      onSave,
      revertDiscardChanges
    ]
  );

  const selectedFunds = Object.values(funds)
    .filter(fund => fund.isSelected)
    .reduce<FundMap>((acc, fund) => {
      if (fund.fund_id) {
        acc[fund.fund_id] = fund;
      }
      return acc;
    }, {});

  return {
    funds: isAIF001 ? funds : selectedFunds,
    allFunds: funds,
    schema,
    isLoading: loadDataToSnowflake.isLoading || isLoading,
    hasChanges,
    previousFunds,
    currentDataKey,
    isLoadingData,
    addRow,
    onSave,
    refetch,
    onDeleteRow,
    onChangeFund,
    onChangeDataKey,
    onClearStaticData,
    isLoadingSchema,
    jurisdictions,
    onNavigateOtherReport,
    onChangeSelectedReportType,
    isViewer,
    canRedo,
    canUndo,
    undo,
    redo,
    isLoadingUser,
    onChangeSelectedFunds,
    withUnsavedChangesGuard,
    reportLoadError,
    isReportLoadError
  };
};
